import type { OfferTemplate } from '@/features/webinar/types';

export interface OfferFormState {
  id: string;
  templateId?: number;
  existingOffer?: OfferTemplate;
}

interface MergeOfferFormStatesOptions {
  createId?: () => string;
  /**
   * Offres dont la sauvegarde est en cours : les listings déclenchés par les thunks de contenu
   * arrivent avant la fin de l'enregistrement et ne portent qu'un template partiel.
   */
  savingIds?: ReadonlySet<string>;
}

/**
 * Une offre en cours de création n'est pas encore dans le store : sans ce report, la
 * reconstruction depuis la liste la démonte et son formulaire revient vide.
 */
export const mergeOfferFormStates = (
  previousStates: OfferFormState[],
  templates: OfferTemplate[],
  { createId = () => crypto.randomUUID(), savingIds }: MergeOfferFormStatesOptions = {},
): OfferFormState[] => {
  const savedStates = templates.map((template) => {
    const previousState = previousStates.find((state) => state.templateId === template.id);
    const isSaving = previousState != null && savingIds?.has(previousState.id) === true;

    return {
      id: previousState?.id ?? createId(),
      templateId: template.id,
      existingOffer: isSaving ? previousState.existingOffer : template,
    };
  });

  const renderedIds = new Set(savedStates.map((state) => state.id));

  // Une offre en attente n'est pas encore dans la liste, et une offre en cours de sauvegarde peut
  // manquer d'un listing émis avant sa création : dans les deux cas, la retirer la ferait
  // disparaître sous l'utilisateur.
  const keptStates = previousStates.filter(
    (state) =>
      !renderedIds.has(state.id) && (state.templateId == null || savingIds?.has(state.id) === true),
  );

  return [...savedStates, ...keptStates];
};
